import React from 'react'
import { Briefcase, Coins, LucideIcon, Star, TrendingUp } from 'lucide-react'

import { useMember } from '@rewardops/auth'
import MetricCard from '@rewardops/replit/src/components/MetricCard'
import { Skeleton, usePAWStore } from '@rewardops/ui'

import { useApi } from '~/components/hooks/use-api'
import { useFormat } from '~/components/hooks/use-format'
import { useTranslation } from '~/app/i18n/client'
import { PAWSectionType } from './paw-section-types'

export const Performance: React.FC<PAWSectionType> = ({
  content,
  data,
}: PAWSectionType) => {
  const { api } = useApi()
  const member = useMember()
  const { formatNumber } = useFormat()
  const { t: translate } = useTranslation()
  const { date, dateLabel } = usePAWStore((state) => state)

  const { data: monthlyTotals, isLoading: monthlyTotalsLoading } =
    api.pawDashboards.getMonthlyTotals.useQuery({
      ...date,
      getMonthly: content?.metadata?.getMonthly ?? false,
      ...(content?.metadata?.params ?? {}),
    } as any)

  const iconsMap: Record<string, LucideIcon> = {
    revenue: TrendingUp,
    unit: Briefcase,
    points: Star,
    default: Star,
  }

  const getTitle = (currency: {
    currencyClassifierName?: string
    currencyName: string
  }) => {
    if (currency?.currencyClassifierName) {
      return `${currency?.currencyName} - ${currency?.currencyClassifierName}`
    }

    return currency?.currencyName
  }

  return (
    <section
      className="mb-8"
      id={content?.sectionId}
      data-testid={content?.sectionId}
    >
      <h2 className="mb-6 text-xl font-semibold">
        {content?.title ?? 'Performance (Last 12 Months)'}
      </h2>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {monthlyTotalsLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-[200px] w-full" />
          ))
        ) : monthlyTotals?.totals?.length ? (
          <React.Fragment>
            {monthlyTotals?.totals?.map((performance: any, index: number) => (
              <React.Fragment
                key={performance?.title?.replaceAll(' ', '_')?.toLowerCase()}
              >
                <MetricCard
                  title={getTitle(performance)}
                  value={formatNumber(performance?.totalAmount) ?? 0}
                  subtitle={
                    dateLabel ??
                    performance?.currencyClassifierName ??
                    performance?.currencyCode ??
                    ''
                  }
                  icon={
                    iconsMap?.[performance?.currencyName?.toLowerCase()] ??
                    iconsMap?.default
                  }
                  variant="default"
                />
              </React.Fragment>
            ))}
          </React.Fragment>
        ) : (
          <div className="text-sm text-slate-800">
            {translate('pawDashboards.noDataFound')}
          </div>
        )}
      </div>
    </section>
  )
}
